import {
  Alert,
  ButtonGroup,
  colors,
  FormStickyHeaderMemo,
  ProgressProps,
  Stack,
  TwoColumnLayoutMemo,
  Typography,
} from '@ui-components/neo.tax';
import React from 'react';

import { useQuestionSections } from '@neo.tax/rd/hooks';
import {
  FilingMethod,
  FormSectionConfiguration,
  FormSections,
  QuestionCode,
  QuestionSectionCode,
  useCurrentUserScopeForCurrentCompany,
  useFlags,
} from '@neo.tax/shared';

import { QuestionSection } from '../company-info';
import { SectionContent } from '../company-info/company-info.styled';
import { usePriorYearQresForms, usePriorYearRevenueForms, useOfficerForms, useReducedCreditForm } from './hooks';

interface CreditCalculationFormsProps {
  filingMethodForms: FormSectionConfiguration;
  filingMethodProgress: ProgressProps;
  filingMethod: FilingMethod | undefined;
  actualBusinessStartIsoDate: string | null;
}

const REDUCED_CREDIT_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const CreditCalculationForms = ({
  actualBusinessStartIsoDate,
  filingMethod,
  filingMethodForms,
  filingMethodProgress,
}: CreditCalculationFormsProps): React.ReactElement => {
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const flags = useFlags();

  const { officerForms } = useOfficerForms();
  const { analyticId, handleReducedCreditChange, isReducedCredit } = useReducedCreditForm();
  const { priorYearQresForms, priorYearQresProgress } = usePriorYearQresForms(filingMethod, actualBusinessStartIsoDate);
  const { priorYearRevenueForms, priorYearRevenueProgress } = usePriorYearRevenueForms();

  const { questionSections, taxYear } = useQuestionSections();
  const visibleQuestionSections = questionSections.filter(
    (questionSection) =>
      questionSection.sectionCode === QuestionSectionCode.CreditCalculation ||
      (flags.enableShortYearQuestions && questionSection.sectionCode === QuestionSectionCode.ShortYearDetails),
  );

  if (limitAccessToFinancialData) {
    return <></>;
  }
  // We actually always have a `taxYear` by the time we get here, we just can't
  // prove it.
  if (!taxYear) {
    return <></>;
  }

  return (
    <Stack alignItems="start" justifyContent="start" direction="column" gap={5}>
      {visibleQuestionSections.map((questionSection) => (
        <QuestionSection
          key={questionSection.id}
          questionSection={questionSection}
          taxYear={taxYear}
          disabledQuestions={[QuestionCode.FirstYearQreAndPyr]}
        />
      ))}
      <TwoColumnLayoutMemo
        leftColumn={
          <FormStickyHeaderMemo
            title="Section 280C Election"
            subtitle="Elect whether to take the reduced credit under Section 280C."
          />
        }
      >
        <SectionContent direction="column" boxShadow="base" borderRadius="md" backgroundColor={colors.white}>
          <Stack direction="column" gap={1} fullWidth>
            <Typography fontSize="sm" fontWeight="medium" color={colors.gray[700]}>
              Are you electing the Reduced Credit under Section 280C?
            </Typography>
            <ButtonGroup
              id={analyticId}
              options={REDUCED_CREDIT_OPTIONS}
              value={isReducedCredit === undefined ? undefined : isReducedCredit ? 'yes' : 'no'}
              onClick={handleReducedCreditChange}
            />
          </Stack>
        </SectionContent>
      </TwoColumnLayoutMemo>
      <TwoColumnLayoutMemo
        leftColumn={<FormStickyHeaderMemo title={officerForms.title} subtitle={officerForms.subtitle} />}
      >
        <FormSections section={officerForms} />
      </TwoColumnLayoutMemo>
      <TwoColumnLayoutMemo
        leftColumn={
          <FormStickyHeaderMemo
            title={filingMethodForms.title}
            subtitle={filingMethodForms.subtitle}
            progress={filingMethodProgress}
          />
        }
      >
        <Stack direction="column" gap={4}>
          <FormSections section={filingMethodForms} />
          {filingMethod === FilingMethod.Regular && !actualBusinessStartIsoDate && (
            <Alert
              severity="warning"
              title="Missing company incorporation date"
              subtitle="Please fill out the company incorporation date on the Company Info page to continue."
            />
          )}
        </Stack>
      </TwoColumnLayoutMemo>
      {priorYearQresForms.forms.length > 0 && (
        <TwoColumnLayoutMemo
          leftColumn={
            <FormStickyHeaderMemo
              title={priorYearQresForms.title}
              subtitle={priorYearQresForms.subtitle}
              progress={priorYearQresProgress}
            />
          }
        >
          <FormSections section={priorYearQresForms} />
        </TwoColumnLayoutMemo>
      )}
      {filingMethod === FilingMethod.Regular &&
        priorYearRevenueForms.forms.length > 0 &&
        actualBusinessStartIsoDate && (
          <TwoColumnLayoutMemo
            leftColumn={
              <FormStickyHeaderMemo
                title={priorYearRevenueForms.title}
                subtitle={priorYearRevenueForms.subtitle}
                progress={priorYearRevenueProgress}
              />
            }
          >
            <FormSections section={priorYearRevenueForms} />
          </TwoColumnLayoutMemo>
        )}
    </Stack>
  );
};
